import { useState, useEffect, useMemo } from "react";

// ─── PEERCAMPUS DESIGN TOKENS (extracted from screenshots) ──
const PC = {
  // Core brand colors
  green:       "#2d7a4f",   // primary green (logo, buttons, badges)
  greenDark:   "#1a5c38",   // hover green
  greenLight:  "#e8f5ee",   // green tint bg
  greenMid:    "#3a8f5f",   // mid green for gradients
  navy:        "#1a2744",   // dark navy (sidebar, footer, hero cards)
  navyDeep:    "#111d33",   // deeper navy
  navyMid:     "#243352",   // mid navy

  // Backgrounds
  bgPage:      "#eef2f7",   // light blue-gray page bg
  bgCard:      "#ffffff",   // white cards
  bgSidebar:   "#1a2744",   // nav sidebar dark navy
  
  // Text
  textPrimary: "#1a2744",   // main headings — navy
  textSub:     "#6b7a99",   // muted/label text
  textLight:   "#9aaac2",   // placeholder
  textWhite:   "#ffffff",
  
  // Borders & surfaces
  border:      "#dce5f0",
  borderLight: "#eef2f7",
  surface:     "#f4f7fc",
  
  // Status
  statusGreen: "#2d7a4f",
  statusRed:   "#c0392b",
  statusOrange:"#e67e22",
  statusBlue:  "#2980b9",

  // Hero gradient (from event dashboard screenshot)
  heroGrad:    "linear-gradient(135deg, #1a2744 0%, #2d7a4f 100%)",
};

// ─── MOCK DATA ───────────────────────────────────────────────
const today = new Date();
const daysAgo = (n) => { const d = new Date(today); d.setDate(d.getDate()-n); return d.toISOString().split("T")[0]; };
const daysFromNow = (n) => { const d = new Date(today); d.setDate(d.getDate()+n); return d.toISOString().split("T")[0]; };

const CAT_COLORS = {
  "Computer Science": "#2d7a4f","Mathematics":"#1a2744","Physics":"#e67e22",
  "Chemistry":"#16a085","Biology":"#27ae60","Literature":"#c0392b",
  "History":"#7f8c8d","Economics":"#f39c12","Engineering":"#2980b9","Medicine":"#8e44ad"
};

const BOOKS = [
  { id:"B001", title:"Introduction to Algorithms", author:"Thomas H. Cormen", isbn:"978-0-262-03384-8", category:"Computer Science", totalCopies:5, availableCopies:3, publishedYear:2022, publisher:"MIT Press", location:"A1-01" },
  { id:"B002", title:"Clean Code", author:"Robert C. Martin", isbn:"978-0-13-235088-4", category:"Computer Science", totalCopies:4, availableCopies:1, publishedYear:2008, publisher:"Prentice Hall", location:"A1-02" },
  { id:"B003", title:"Calculus: Early Transcendentals", author:"James Stewart", isbn:"978-1-285-74155-0", category:"Mathematics", totalCopies:6, availableCopies:4, publishedYear:2020, publisher:"Cengage Learning", location:"B2-01" },
  { id:"B004", title:"University Physics", author:"Hugh D. Young", isbn:"978-0-13-398124-8", category:"Physics", totalCopies:4, availableCopies:2, publishedYear:2019, publisher:"Pearson", location:"C3-01" },
  { id:"B005", title:"Organic Chemistry", author:"Paula Y. Bruice", isbn:"978-0-13-404228-2", category:"Chemistry", totalCopies:3, availableCopies:0, publishedYear:2021, publisher:"Pearson", location:"D4-01" },
  { id:"B006", title:"Campbell Biology", author:"Jane B. Reece", isbn:"978-0-13-409341-3", category:"Biology", totalCopies:5, availableCopies:3, publishedYear:2020, publisher:"Pearson", location:"E5-01" },
  { id:"B007", title:"The Great Gatsby", author:"F. Scott Fitzgerald", isbn:"978-0-7432-7356-5", category:"Literature", totalCopies:8, availableCopies:6, publishedYear:1925, publisher:"Scribner", location:"F6-01" },
  { id:"B008", title:"Sapiens: A Brief History", author:"Yuval Noah Harari", isbn:"978-0-06-231609-7", category:"History", totalCopies:4, availableCopies:2, publishedYear:2015, publisher:"Harper", location:"G7-01" },
  { id:"B009", title:"Principles of Economics", author:"N. Gregory Mankiw", isbn:"978-0-357-03831-4", category:"Economics", totalCopies:5, availableCopies:3, publishedYear:2021, publisher:"Cengage", location:"H8-01" },
  { id:"B010", title:"Engineering Mechanics", author:"R.C. Hibbeler", isbn:"978-0-13-411542-6", category:"Engineering", totalCopies:4, availableCopies:1, publishedYear:2022, publisher:"Pearson", location:"I9-01" },
  { id:"B011", title:"Design Patterns", author:"Gang of Four", isbn:"978-0-20-163361-5", category:"Computer Science", totalCopies:3, availableCopies:2, publishedYear:1994, publisher:"Addison-Wesley", location:"A1-03" },
  { id:"B012", title:"Linear Algebra", author:"Gilbert Strang", isbn:"978-0-98-023280-3", category:"Mathematics", totalCopies:4, availableCopies:4, publishedYear:2016, publisher:"Wellesley-Cambridge", location:"B2-02" },
  { id:"B013", title:"Gray's Anatomy", author:"Henry Gray", isbn:"978-0-70-204790-3", category:"Medicine", totalCopies:3, availableCopies:1, publishedYear:2020, publisher:"Elsevier", location:"J10-01" },
  { id:"B014", title:"The Pragmatic Programmer", author:"David Thomas", isbn:"978-0-13-595705-9", category:"Computer Science", totalCopies:3, availableCopies:3, publishedYear:2019, publisher:"Addison-Wesley", location:"A1-04" },
  { id:"B015", title:"To Kill a Mockingbird", author:"Harper Lee", isbn:"978-0-06-112008-4", category:"Literature", totalCopies:6, availableCopies:5, publishedYear:1960, publisher:"Harper Perennial", location:"F6-02" },
];

const MEMBERS = [
  { id:"M001", name:"Alice Johnson", email:"alice.j@campus.edu", studentId:"STU2021001", department:"Computer Science", year:3, memberSince:"2021-08-15", status:"active", totalBorrowed:24, currentlyBorrowed:2 },
  { id:"M002", name:"Bob Martinez", email:"bob.m@campus.edu", studentId:"STU2022015", department:"Mathematics", year:2, memberSince:"2022-08-20", status:"active", totalBorrowed:18, currentlyBorrowed:1 },
  { id:"M003", name:"Carol White", email:"carol.w@campus.edu", studentId:"STU2020032", department:"Physics", year:4, memberSince:"2020-08-10", status:"active", totalBorrowed:36, currentlyBorrowed:3 },
  { id:"M004", name:"David Kim", email:"david.k@campus.edu", studentId:"STU2023007", department:"Engineering", year:1, memberSince:"2023-08-18", status:"active", totalBorrowed:8, currentlyBorrowed:0 },
  { id:"M005", name:"Emma Davis", email:"emma.d@campus.edu", studentId:"STU2021045", department:"Literature", year:3, memberSince:"2021-08-15", status:"suspended", totalBorrowed:22, currentlyBorrowed:1 },
  { id:"M006", name:"Frank Wilson", email:"frank.w@campus.edu", studentId:"STU2022033", department:"Biology", year:2, memberSince:"2022-08-20", status:"active", totalBorrowed:15, currentlyBorrowed:2 },
  { id:"M007", name:"Grace Lee", email:"grace.l@campus.edu", studentId:"STU2020018", department:"Chemistry", year:4, memberSince:"2020-08-10", status:"active", totalBorrowed:41, currentlyBorrowed:1 },
  { id:"M008", name:"Henry Brown", email:"henry.b@campus.edu", studentId:"STU2023021", department:"Economics", year:1, memberSince:"2023-08-18", status:"active", totalBorrowed:5, currentlyBorrowed:1 },
];

const TXNS_INIT = [
  { id:"T001", bookId:"B001", memberId:"M001", bookTitle:"Introduction to Algorithms", memberName:"Alice Johnson", issueDate:daysAgo(12), dueDate:daysFromNow(2), returnDate:null, status:"issued", fine:0 },
  { id:"T002", bookId:"B002", memberId:"M001", bookTitle:"Clean Code", memberName:"Alice Johnson", issueDate:daysAgo(20), dueDate:daysAgo(6), returnDate:null, status:"overdue", fine:18 },
  { id:"T003", bookId:"B003", memberId:"M002", bookTitle:"Calculus: Early Transcendentals", memberName:"Bob Martinez", issueDate:daysAgo(8), dueDate:daysFromNow(6), returnDate:null, status:"issued", fine:0 },
  { id:"T004", bookId:"B004", memberId:"M003", bookTitle:"University Physics", memberName:"Carol White", issueDate:daysAgo(25), dueDate:daysAgo(11), returnDate:null, status:"overdue", fine:33 },
  { id:"T005", bookId:"B006", memberId:"M003", bookTitle:"Campbell Biology", memberName:"Carol White", issueDate:daysAgo(5), dueDate:daysFromNow(9), returnDate:null, status:"issued", fine:0 },
  { id:"T006", bookId:"B007", memberId:"M003", bookTitle:"The Great Gatsby", memberName:"Carol White", issueDate:daysAgo(30), dueDate:daysAgo(16), returnDate:null, status:"overdue", fine:48 },
  { id:"T007", bookId:"B005", memberId:"M005", bookTitle:"Organic Chemistry", memberName:"Emma Davis", issueDate:daysAgo(18), dueDate:daysAgo(4), returnDate:null, status:"overdue", fine:12 },
  { id:"T008", bookId:"B008", memberId:"M006", bookTitle:"Sapiens: A Brief History", memberName:"Frank Wilson", issueDate:daysAgo(4), dueDate:daysFromNow(10), returnDate:null, status:"issued", fine:0 },
  { id:"T009", bookId:"B010", memberId:"M006", bookTitle:"Engineering Mechanics", memberName:"Frank Wilson", issueDate:daysAgo(7), dueDate:daysFromNow(7), returnDate:null, status:"issued", fine:0 },
  { id:"T010", bookId:"B013", memberId:"M007", bookTitle:"Gray's Anatomy", memberName:"Grace Lee", issueDate:daysAgo(6), dueDate:daysFromNow(8), returnDate:null, status:"issued", fine:0 },
  { id:"T011", bookId:"B009", memberId:"M008", bookTitle:"Principles of Economics", memberName:"Henry Brown", issueDate:daysAgo(3), dueDate:daysFromNow(11), returnDate:null, status:"issued", fine:0 },
  { id:"T012", bookId:"B011", memberId:"M001", bookTitle:"Design Patterns", memberName:"Alice Johnson", issueDate:daysAgo(30), dueDate:daysAgo(16), returnDate:daysAgo(14), status:"returned", fine:6 },
  { id:"T013", bookId:"B012", memberId:"M004", bookTitle:"Linear Algebra", memberName:"David Kim", issueDate:daysAgo(28), dueDate:daysAgo(14), returnDate:daysAgo(15), status:"returned", fine:0 },
  { id:"T014", bookId:"B015", memberId:"M007", bookTitle:"To Kill a Mockingbird", memberName:"Grace Lee", issueDate:daysAgo(21), dueDate:daysAgo(7), returnDate:daysAgo(8), status:"returned", fine:0 },
  { id:"T015", bookId:"B014", memberId:"M002", bookTitle:"The Pragmatic Programmer", memberName:"Bob Martinez", issueDate:daysAgo(35), dueDate:daysAgo(21), returnDate:daysAgo(20), status:"returned", fine:3 },
];

const MONTHLY = [
  {month:"Aug",borrowed:42,returned:38},{month:"Sep",borrowed:65,returned:58},
  {month:"Oct",borrowed:78,returned:71},{month:"Nov",borrowed:55,returned:62},
  {month:"Dec",borrowed:32,returned:45},{month:"Jan",borrowed:48,returned:41},
  {month:"Feb",borrowed:72,returned:65},{month:"Mar",borrowed:61,returned:58},
];

const CATS = ["Computer Science","Mathematics","Physics","Chemistry","Biology","Literature","History","Economics","Engineering","Medicine"];
const genId = (p) => `${p}${Date.now().toString().slice(-6)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN",{year:"numeric",month:"short",day:"numeric"}) : "—";
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString("en-CA") : "—"; // YYYY-MM-DD

// ─── SVG ICONS ───────────────────────────────────────────────
const Ico = ({d,s=18,c="currentColor",sw=2}) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <path d={d}/>
  </svg>
);
const I = {
  book:      "M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 004 17V5a2 2 0 012-2h12a2 2 0 012 2v12M4 19.5V21",
  users:     "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  alert:     "M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01",
  plus:      "M12 5v14M5 12h14",
  search:    "M11 17a6 6 0 100-12 6 6 0 000 12zM21 21l-4.35-4.35",
  edit:      "M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z",
  trash:     "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6",
  return:    "M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15",
  calendar:  "M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM16 2v4M8 2v4M3 10h18",
  dashboard: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  close:     "M18 6L6 18M6 6l12 12",
  chevron:   "M9 18l6-6-6-6",
  back:      "M19 12H5M12 5l-7 7 7 7",
  download:  "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3",
  eye:       "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 100 6 3 3 0 000-6z",
  dots:      "M12 5h.01M12 12h.01M12 19h.01",
  check:     "M20 6L9 17l-5-5",
  announce:  "M22 8.5c0 2.76-2.24 5-5 5-1.22 0-2.33-.44-3.19-1.16L5 15V9l8.81 2.66A4.97 4.97 0 0117 10.5M2 9v6",
};

// ─── MINI CHART (pure SVG) ───────────────────────────────────
const MiniBarChart = ({ data }) => {
  const max = Math.max(...data.map(d => Math.max(d.borrowed, d.returned)));
  const W = 400, H = 120, pad = 30, barW = 18, gap = (W - pad*2) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:120}}>
      {data.map((d, i) => {
        const x = pad + i * gap;
        const bH = (d.borrowed / max) * (H - 40);
        const rH = (d.returned / max) * (H - 40);
        return (
          <g key={i}>
            <rect x={x} y={H-20-bH} width={barW} height={bH} rx={3} fill={PC.green} opacity={0.85}/>
            <rect x={x+barW+2} y={H-20-rH} width={barW} height={rH} rx={3} fill={PC.navy} opacity={0.6}/>
            <text x={x+barW} y={H-4} textAnchor="middle" fontSize="9" fill={PC.textSub}>{d.month}</text>
          </g>
        );
      })}
    </svg>
  );
};

const MiniDonut = ({ data }) => {
  const total = data.reduce((s,d)=>s+d.value,0);
  let angle = -Math.PI/2;
  const cx=80, cy=80, r=55, ir=32;
  const slices = data.map(d => {
    const a = (d.value/total)*Math.PI*2;
    const x1=cx+r*Math.cos(angle), y1=cy+r*Math.sin(angle);
    angle += a;
    const x2=cx+r*Math.cos(angle), y2=cy+r*Math.sin(angle);
    const ix1=cx+ir*Math.cos(angle-a), iy1=cy+ir*Math.sin(angle-a);
    const ix2=cx+ir*Math.cos(angle), iy2=cy+ir*Math.sin(angle);
    const large = a > Math.PI ? 1 : 0;
    return { path:`M${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} L${ix2},${iy2} A${ir},${ir} 0 ${large},0 ${ix1},${iy1} Z`, color:d.color, name:d.name, value:d.value };
  });
  return (
    <svg viewBox="0 0 160 160" style={{width:160,height:160}}>
      {slices.map((s,i)=><path key={i} d={s.path} fill={s.color} stroke="white" strokeWidth={1.5}/>)}
      <text x={cx} y={cy-4} textAnchor="middle" fontSize="14" fontWeight="700" fill={PC.navy}>{total}</text>
      <text x={cx} y={cy+12} textAnchor="middle" fontSize="8" fill={PC.textSub}>titles</text>
    </svg>
  );
};

// ─── STATUS BADGE (matching PeerCampus green "ACTIVE" pill) ──
const StatusBadge = ({ status }) => {
  const cfg = {
    active:   { bg:"#2d7a4f", text:"#fff" },
    issued:   { bg:"#2980b9", text:"#fff" },
    overdue:  { bg:"#c0392b", text:"#fff" },
    returned: { bg:"#27ae60", text:"#fff" },
    suspended:{ bg:"#e74c3c", text:"#fff" },
    open:     { bg:"#2d7a4f", text:"#fff" },
  };
  const s = cfg[status] || { bg:"#95a5a6", text:"#fff" };
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,
      padding:"3px 10px",borderRadius:99,fontSize:11,fontWeight:700,
      background:s.bg,color:s.text,textTransform:"uppercase",letterSpacing:0.5}}>
      {status}
    </span>
  );
};

// ─── TOP NAVBAR (matches PeerCampus screenshots exactly) ─────
const Navbar = ({ activePage }) => (
  <header style={{background:"white",borderBottom:`1px solid ${PC.border}`,
    display:"flex",alignItems:"center",justifyContent:"space-between",
    padding:"0 32px",height:60,position:"sticky",top:0,zIndex:100,
    boxShadow:"0 1px 4px rgba(26,39,68,0.07)"}}>
    {/* Logo + Brand */}
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      {/* PeerCampus logo circle */}
      <div style={{width:36,height:36,borderRadius:"50%",background:PC.green,
        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <svg width={22} height={22} viewBox="0 0 36 36" fill="none">
          <circle cx={18} cy={18} r={18} fill={PC.green}/>
          {/* Graduation cap */}
          <path d="M8 16l10-5 10 5-10 5z" fill={PC.navy} opacity={0.9}/>
          <path d="M14 18.5v4c0 1.5 2 2.5 4 2.5s4-1 4-2.5v-4" stroke="white" strokeWidth={1.5} fill="none"/>
          <circle cx={18} cy={21} r={2} fill="white" opacity={0.8}/>
          <path d="M14 21 L10 23" stroke="white" strokeWidth={1.3}/>
          <path d="M22 21 L26 23" stroke="white" strokeWidth={1.3}/>
        </svg>
      </div>
      <span style={{fontSize:20,fontWeight:800,color:PC.navy,letterSpacing:-0.5}}>PeerCampus</span>
      <span style={{fontSize:10,fontWeight:700,color:"white",background:PC.green,
        borderRadius:99,padding:"2px 8px",letterSpacing:0.3}}>AI-Powered</span>
    </div>

    {/* Right: user avatar */}
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      <div style={{width:34,height:34,borderRadius:"50%",background:PC.navy,
        display:"flex",alignItems:"center",justifyContent:"center",
        fontSize:13,fontWeight:700,color:"white"}}>H</div>
      <span style={{fontSize:14,fontWeight:600,color:PC.navy}}>harsit</span>
    </div>
  </header>
);

// ─── FOOTER (dark, matches PeerCampus) ───────────────────────
const Footer = () => (
  <footer style={{background:PC.navy,padding:"18px 40px",marginTop:"auto",
    display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <div style={{width:26,height:26,borderRadius:"50%",background:PC.green,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Ico d={I.book} s={13} c="white"/>
      </div>
      <div>
        <div style={{fontSize:13,fontWeight:700,color:"white"}}>PeerCampus</div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.45)"}}>Signed-in experience</div>
      </div>
    </div>
    <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
      {["Dashboard","Library","Books","Members","Privacy","Terms"].map(l=>(
        <span key={l} style={{fontSize:12,color:"rgba(255,255,255,0.55)",cursor:"pointer"}}>
          {l}
        </span>
      ))}
    </div>
    <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>© 2026 PeerCampus</div>
  </footer>
);

// ─── BREADCRUMB (matches "Dashboard > Clubs" style) ──────────
const Breadcrumb = ({ items, onBack }) => (
  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
    {onBack && (
      <button onClick={onBack} style={{display:"flex",alignItems:"center",gap:6,
        border:`1px solid ${PC.border}`,borderRadius:8,padding:"5px 12px",
        background:"white",cursor:"pointer",fontSize:13,fontWeight:500,color:PC.textPrimary}}>
        <Ico d={I.back} s={14} c={PC.textPrimary}/>
        Back
      </button>
    )}
    {items.map((item, i) => (
      <span key={i} style={{display:"flex",alignItems:"center",gap:8}}>
        {i > 0 && <Ico d={I.chevron} s={14} c={PC.textSub}/>}
        <span style={{fontSize:14,color:i===items.length-1?PC.navy:PC.green,fontWeight:i===items.length-1?600:400,
          cursor:i<items.length-1?"pointer":"default"}}
          onClick={item.onClick}>
          {item.label}
        </span>
      </span>
    ))}
  </div>
);

// ─── HERO BANNER (green-navy gradient, matches Event Dashboard) ─
const HeroBanner = ({ title, subtitle, stats, actions }) => (
  <div style={{background:PC.heroGrad,borderRadius:16,padding:"28px 32px",marginBottom:28,
    boxShadow:"0 4px 24px rgba(26,39,68,0.18)"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <div>
        <h1 style={{margin:0,fontSize:26,fontWeight:800,color:"white",letterSpacing:-0.5}}>{title}</h1>
        <p style={{margin:"4px 0 0",fontSize:13,color:"rgba(255,255,255,0.65)"}}>{subtitle}</p>
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {actions}
      </div>
    </div>
    {/* Stat cards inside banner — white cards like in Event Dashboard */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
      {stats.map((s,i)=>(
        <div key={i} style={{background:"rgba(255,255,255,0.12)",backdropFilter:"blur(8px)",
          borderRadius:12,padding:"16px 18px",border:"1px solid rgba(255,255,255,0.15)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
            <div style={{width:32,height:32,borderRadius:8,background:"rgba(255,255,255,0.12)",
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Ico d={s.icon} s={16} c="rgba(255,255,255,0.85)"/>
            </div>
            <span style={{fontSize:26,fontWeight:800,color:"white"}}>{s.value}</span>
          </div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.65)",fontWeight:500}}>{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

// ─── BTN VARIANTS ────────────────────────────────────────────
const Btn = ({children,variant="primary",onClick,icon,size="md",disabled}) => {
  const styles = {
    primary:{background:PC.navy,color:"white",border:"none"},
    navy:{background:PC.navy,color:"white",border:"none"},
    green:{background:PC.green,color:"white",border:"none"},
    outline:{background:"white",color:PC.navy,border:`1.5px solid ${PC.border}`},
    danger:{background:"#c0392b",color:"white",border:"none"},
    ghost:{background:"transparent",color:PC.green,border:"none"},
  };
  const selectedStyle = styles[variant] || styles.primary;
  const sz = size==="sm"?{padding:"5px 12px",fontSize:12}:{padding:"8px 18px",fontSize:13};
  return (
    <button onClick={onClick} disabled={disabled}
      style={{display:"flex",alignItems:"center",gap:6,borderRadius:8,fontWeight:600,cursor:disabled?"not-allowed":"pointer",
        transition:"all 0.15s",opacity:disabled?0.5:1,...selectedStyle,...sz}}>
      {icon && <Ico d={icon} s={14} c={selectedStyle.color}/>}
      {children}
    </button>
  );
};

// ─── FORM FIELD ──────────────────────────────────────────────
const Field = ({label,value,onChange,type="text",options,required,error,placeholder}) => (
  <div style={{marginBottom:2}}>
    <label style={{display:"block",fontSize:11,fontWeight:600,color:PC.textSub,marginBottom:4,
      textTransform:"uppercase",letterSpacing:0.5}}>{label}{required&&" *"}</label>
    {options ? (
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{width:"100%",padding:"8px 12px",borderRadius:8,border:`1.5px solid ${error?PC.statusRed:PC.border}`,
          fontSize:13,color:PC.textPrimary,background:"white",outline:"none",boxSizing:"border-box"}}>
        {options.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    ):(
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{width:"100%",padding:"8px 12px",borderRadius:8,border:`1.5px solid ${error?PC.statusRed:PC.border}`,
          fontSize:13,color:PC.textPrimary,outline:"none",boxSizing:"border-box"}}/>
    )}
    {error&&<div style={{fontSize:11,color:PC.statusRed,marginTop:3}}>{error}</div>}
  </div>
);

// ─── MODAL ───────────────────────────────────────────────────
const Modal = ({open,onClose,title,children,footer,width=480}) => {
  if(!open) return null;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(26,39,68,0.45)",zIndex:999,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"white",borderRadius:16,width:"100%",maxWidth:width,
        maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(26,39,68,0.3)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          padding:"20px 24px",borderBottom:`1px solid ${PC.border}`}}>
          <h2 style={{margin:0,fontSize:17,fontWeight:800,color:PC.navy}}>{title}</h2>
          <button onClick={onClose} style={{border:"none",background:"transparent",cursor:"pointer",color:PC.textSub}}>
            <Ico d={I.close} s={18}/>
          </button>
        </div>
        <div style={{padding:"20px 24px"}}>{children}</div>
        {footer&&<div style={{padding:"16px 24px",borderTop:`1px solid ${PC.border}`,
          display:"flex",justifyContent:"flex-end",gap:10}}>{footer}</div>}
      </div>
    </div>
  );
};

// ─── TOAST ───────────────────────────────────────────────────
const Toast = ({msg,type,onClose}) => {
  useEffect(()=>{const t=setTimeout(onClose,3000);return()=>clearTimeout(t);},[onClose]);
  if(!msg) return null;
  const bg = type==="error"?"#c0392b":type==="warning"?"#e67e22":PC.green;
  return (
    <div style={{position:"fixed",bottom:24,right:24,zIndex:9999,background:bg,color:"white",
      borderRadius:10,padding:"12px 20px",fontSize:13,fontWeight:600,
      boxShadow:"0 8px 24px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",gap:8,
      animation:"slideIn 0.2s ease"}}>
      <Ico d={type==="error"?I.alert:I.check} s={16} c="white"/>
      {msg}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function PeerCampusLibrary() {
  const [page, setPage]         = useState("dashboard");
  const [books, setBooks]       = useState(BOOKS.map(b=>({...b})));
  const [members]               = useState(MEMBERS);
  const [txns, setTxns]         = useState(TXNS_INIT.map(t=>({...t})));
  const [toast, setToast]       = useState(null);
  const [loading, setLoading]   = useState(true);

  // Books page state
  const [bookSearch, setBookSearch]   = useState("");
  const [catFilter, setCatFilter]     = useState("All");
  const [bookDialog, setBookDialog]   = useState({open:false,edit:null});
  const [delBook, setDelBook]         = useState(null);

  // Transactions page state
  const [txnFilter, setTxnFilter]   = useState("all");
  const [issueDialog, setIssueDialog] = useState(false);
  const [issueForm, setIssueForm]     = useState({bookId:"",memberId:"",dueDays:14});
  const [issueErr, setIssueErr]       = useState("");

  // Members page state
  const [memSearch, setMemSearch]     = useState("");
  const [selMember, setSelMember]     = useState(null);

  // Book form state
  const emptyBook = {title:"",author:"",isbn:"",category:"Computer Science",totalCopies:1,publishedYear:2024,publisher:"",location:""};
  const [bForm, setBForm] = useState(emptyBook);
  const [bErrors, setBErrors] = useState({});

  useEffect(()=>{setTimeout(()=>setLoading(false),600);},[]);

  const notify = (msg,type="success") => setToast({msg,type});

  // ── Stats
  const stats = useMemo(()=>({
    totalBooks: books.reduce((s,b)=>s+b.totalCopies,0),
    uniqueTitles: books.length,
    issued: txns.filter(t=>t.status==="issued"||t.status==="overdue").length,
    overdue: txns.filter(t=>t.status==="overdue").length,
    activeMembers: members.filter(m=>m.status==="active").length,
  }),[books,members,txns]);

  // ── Filtered books
  const filtBooks = useMemo(()=>books.filter(b=>{
    const q=bookSearch.toLowerCase();
    return(!q||b.title.toLowerCase().includes(q)||b.author.toLowerCase().includes(q)||b.isbn.includes(q))
      &&(catFilter==="All"||b.category===catFilter);
  }),[books,bookSearch,catFilter]);

  // ── Filtered txns
  const filtTxns = useMemo(()=>txnFilter==="all"?txns:txns.filter(t=>t.status===txnFilter),[txns,txnFilter]);

  // ── Handlers
  const saveBook = () => {
    const e={};
    if(!bForm.title.trim()) e.title="Required";
    if(!bForm.author.trim()) e.author="Required";
    if(!bForm.isbn.trim()) e.isbn="Required";
    if(bForm.totalCopies<1) e.totalCopies="Min 1";
    if(Object.keys(e).length){setBErrors(e);return;}
    if(bForm.id){
      setBooks(prev=>prev.map(b=>b.id===bForm.id?{...b,...bForm}:b));
      notify("Book updated successfully!");
    } else {
      const nb={...bForm,id:genId("B"),availableCopies:Number(bForm.totalCopies)};
      setBooks(prev=>[...prev,nb]);
      notify("Book added successfully!");
    }
    setBookDialog({open:false,edit:null}); setBErrors({});
  };

  const deleteBook = () => {
    setBooks(prev=>prev.filter(b=>b.id!==delBook.id));
    setDelBook(null); notify("Book deleted.","warning");
  };

  const issueBook = () => {
    if(!issueForm.bookId||!issueForm.memberId){setIssueErr("Please select both book and member.");return;}
    const bk=books.find(b=>b.id===issueForm.bookId);
    const mb=members.find(m=>m.id===issueForm.memberId);
    const nt={id:genId("T"),bookId:bk.id,memberId:mb.id,bookTitle:bk.title,memberName:mb.name,
      issueDate:new Date().toISOString().split("T")[0],
      dueDate:new Date(Date.now()+issueForm.dueDays*86400000).toISOString().split("T")[0],
      returnDate:null,status:"issued",fine:0};
    setTxns(prev=>[...prev,nt]);
    setBooks(prev=>prev.map(b=>b.id===bk.id?{...b,availableCopies:b.availableCopies-1}:b));
    setIssueDialog(false); setIssueForm({bookId:"",memberId:"",dueDays:14}); setIssueErr("");
    notify(`"${bk.title}" issued to ${mb.name}!`);
  };

  const returnBook = (txnId) => {
    const t=txns.find(x=>x.id===txnId);
    const overdueD=Math.max(0,Math.floor((new Date()-new Date(t.dueDate))/86400000));
    const fine=overdueD*3;
    setTxns(prev=>prev.map(x=>x.id===txnId?{...x,returnDate:new Date().toISOString().split("T")[0],status:"returned",fine}:x));
    setBooks(prev=>prev.map(b=>b.id===t.bookId?{...b,availableCopies:b.availableCopies+1}:b));
    notify(`Returned!${fine>0?` Fine: $${fine}`:""}`,fine>0?"warning":"success");
  };

  if(loading) return (
    <div style={{height:"100vh",background:PC.bgPage,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{width:52,height:52,borderRadius:"50%",background:PC.green,margin:"0 auto 16px",
          display:"flex",alignItems:"center",justifyContent:"center",
          animation:"spin 1s linear infinite"}}>
          <Ico d={I.book} s={24} c="white"/>
        </div>
        <div style={{fontSize:15,fontWeight:600,color:PC.navy}}>Loading Library...</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ─── TABLE HEADER CELL ─────────────────────────────────────
  const TH = ({children,w}) => (
    <th style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:700,
      color:PC.textSub,textTransform:"uppercase",letterSpacing:0.6,
      borderBottom:`2px solid ${PC.border}`,width:w,whiteSpace:"nowrap"}}>
      {children}
    </th>
  );
  const TD = ({children,bold,color,maxW}) => (
    <td style={{padding:"12px 14px",fontSize:13,fontWeight:bold?600:400,
      color:color||PC.textPrimary,borderBottom:`1px solid ${PC.borderLight}`,
      maxWidth:maxW,overflow:maxW?"hidden":undefined,textOverflow:maxW?"ellipsis":undefined,whiteSpace:maxW?"nowrap":undefined}}>
      {children}
    </td>
  );

  // ═══════════ PAGES ══════════════════════════════════════════

  // ── DASHBOARD ──────────────────────────────────────────────
  const DashboardPage = () => (
    <div>
      {/* Quick Actions (like PeerCampus dashboard) */}
      <div style={{marginBottom:24}}>
        <HeroBanner
          title="Library Dashboard"
          subtitle="Manage and oversee all library resources and borrowing activity"
          actions={<>
            <Btn variant="outline" icon={I.download} size="sm">Export PDF</Btn>
            <Btn variant="green" icon={I.plus} size="sm" onClick={()=>setPage("issue")}>Issue Book</Btn>
          </>}
          stats={[
            {icon:I.book,value:stats.totalBooks,label:"Total Books"},
            {icon:I.calendar,value:stats.issued,label:"Issued Books"},
            {icon:I.alert,value:stats.overdue,label:"Overdue Books"},
            {icon:I.users,value:stats.activeMembers,label:"Active Members"},
          ]}
        />
      </div>

      {/* Charts row */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 340px",gap:20,marginBottom:20}}>
        {/* Monthly chart */}
        <div style={{background:"white",borderRadius:14,padding:"22px 24px",border:`1px solid ${PC.border}`,
          boxShadow:"0 2px 8px rgba(26,39,68,0.05)"}}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:15,fontWeight:800,color:PC.navy}}>Monthly Borrowing Activity</div>
            <div style={{fontSize:12,color:PC.textSub}}>Books issued and returned per month</div>
          </div>
          <div style={{display:"flex",gap:16,marginBottom:10}}>
            {[{c:PC.green,l:"Issued"},{c:PC.navy,l:"Returned"}].map(({c,l})=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:10,height:10,borderRadius:2,background:c}}/>
                <span style={{fontSize:11,color:PC.textSub}}>{l}</span>
              </div>
            ))}
          </div>
          <MiniBarChart data={MONTHLY}/>
        </div>
        {/* Donut chart */}
        <div style={{background:"white",borderRadius:14,padding:"22px 24px",border:`1px solid ${PC.border}`,
          boxShadow:"0 2px 8px rgba(26,39,68,0.05)"}}>
          <div style={{fontSize:15,fontWeight:800,color:PC.navy,marginBottom:4}}>Books by Category</div>
          <div style={{fontSize:12,color:PC.textSub,marginBottom:12}}>Subject distribution</div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <MiniDonut data={Object.entries(CAT_COLORS).slice(0,7).map(([name,color])=>({name,color,value:books.filter(b=>b.category===name).length})).filter(d=>d.value>0)}/>
            <div style={{width:"100%",marginTop:8}}>
              {Object.entries(CAT_COLORS).slice(0,5).map(([name,color])=>{
                const count=books.filter(b=>b.category===name).length;
                if(!count) return null;
                return (
                  <div key={name} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:color,flexShrink:0}}/>
                    <span style={{fontSize:11,color:PC.textSub,flex:1}}>{name}</span>
                    <span style={{fontSize:11,fontWeight:700,color:PC.navy}}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity — table like "All Events" section */}
      <div style={{background:"white",borderRadius:14,border:`1px solid ${PC.border}`,
        boxShadow:"0 2px 8px rgba(26,39,68,0.05)"}}>
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${PC.border}`,
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:PC.navy}}>Recent Transactions</div>
            <div style={{fontSize:12,color:PC.textSub}}>{txns.filter(t=>t.status!=="returned").length} active loans</div>
          </div>
          <Btn variant="ghost" onClick={()=>setPage("issue")} size="sm">View All →</Btn>
        </div>
        <div style={{overflow:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead style={{background:PC.surface}}>
              <tr><TH>Book</TH><TH>Member</TH><TH>Issue Date</TH><TH>Due Date</TH><TH>Status</TH><TH>Fine</TH></tr>
            </thead>
            <tbody>
              {txns.filter(t=>t.status!=="returned").slice(0,7).map(t=>(
                <tr key={t.id} style={{background:t.status==="overdue"?"#fff5f5":"white"}}>
                  <TD bold maxW={200}>{t.bookTitle}</TD>
                  <TD color={PC.textSub}>{t.memberName}</TD>
                  <TD color={PC.textSub}>{fmtDateShort(t.issueDate)}</TD>
                  <TD color={t.status==="overdue"?PC.statusRed:PC.textSub} bold={t.status==="overdue"}>{fmtDateShort(t.dueDate)}</TD>
                  <TD><StatusBadge status={t.status}/></TD>
                  <TD color={t.fine>0?PC.statusRed:PC.textSub} bold={t.fine>0}>{t.fine>0?`$${t.fine}`:"—"}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ── BOOKS PAGE (matches "Manage Clubs" layout) ─────────────
  const BooksPage = () => (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <Breadcrumb items={[{label:"Dashboard",onClick:()=>setPage("dashboard")},{label:"Books"}]} onBack={()=>setPage("dashboard")}/>
          <h1 style={{margin:"8px 0 4px",fontSize:24,fontWeight:800,color:PC.navy}}>Manage Books</h1>
          <p style={{margin:0,fontSize:13,color:PC.textSub}}>Add, edit, and manage the library collection.</p>
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="outline" icon={I.download} size="sm">Export PDF ({filtBooks.length})</Btn>
          <Btn variant="navy" icon={I.plus} onClick={()=>{setBForm(emptyBook);setBErrors({});setBookDialog({open:true,edit:null})}}>
            + Add New Book
          </Btn>
        </div>
      </div>

      {/* Table card — matches PeerCampus club table exactly */}
      <div style={{background:"white",borderRadius:14,border:`1px solid ${PC.border}`,
        boxShadow:"0 2px 8px rgba(26,39,68,0.04)"}}>
        <div style={{padding:"20px 24px",borderBottom:`1px solid ${PC.border}`}}>
          <div style={{fontSize:16,fontWeight:800,color:PC.navy}}>All Books</div>
          <div style={{fontSize:13,color:PC.textSub,marginBottom:16}}>{filtBooks.length} books</div>
          {/* Search + filter row */}
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <div style={{position:"relative",flex:"1 1 260px"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)"}}>
                <Ico d={I.search} s={15} c={PC.textSub}/>
              </span>
              <input placeholder="Search books..." value={bookSearch} onChange={e=>setBookSearch(e.target.value)}
                style={{width:"100%",padding:"8px 12px 8px 34px",borderRadius:8,border:`1.5px solid ${PC.border}`,
                  fontSize:13,color:PC.textPrimary,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:12,fontWeight:600,color:PC.textSub}}>Category</span>
              <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
                style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${PC.border}`,
                  fontSize:13,color:PC.textPrimary,background:"white",cursor:"pointer"}}>
                <option value="All">All</option>
                {CATS.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {(bookSearch||catFilter!=="All")&&(
              <button onClick={()=>{setBookSearch("");setCatFilter("All");}}
                style={{background:"none",border:"none",color:PC.textSub,cursor:"pointer",fontSize:13}}>Clear</button>
            )}
          </div>
        </div>

        <div style={{overflow:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead style={{background:PC.surface}}>
              <tr>
                <TH>Book Name</TH><TH>Author</TH><TH>Category</TH>
                <TH>Copies</TH><TH>Available</TH><TH>Location</TH><TH>Status</TH><TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtBooks.map(b=>(
                <tr key={b.id} style={{"&:hover":{background:PC.surface}}}>
                  <TD bold>{b.title}</TD>
                  <TD color={PC.textSub}>{b.author}</TD>
                  <TD>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5,
                      padding:"3px 10px",borderRadius:99,fontSize:11,fontWeight:600,
                      background:`${CAT_COLORS[b.category]}18`,color:CAT_COLORS[b.category]}}>
                      {b.category}
                    </span>
                  </TD>
                  <TD color={PC.textSub}>{b.totalCopies}</TD>
                  <TD>
                    <span style={{fontWeight:700,color:b.availableCopies===0?PC.statusRed:PC.green,fontSize:14}}>
                      {b.availableCopies}
                    </span>
                  </TD>
                  <TD color={PC.textSub}>{b.location||"—"}</TD>
                  <TD><StatusBadge status={b.availableCopies>0?"active":"overdue"}/></TD>
                  <td style={{padding:"12px 14px",borderBottom:`1px solid ${PC.borderLight}`}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>{setBForm({...b});setBErrors({});setBookDialog({open:true,edit:b});}}
                        style={{background:"none",border:"none",cursor:"pointer",color:PC.textSub,padding:4,borderRadius:6,
                          display:"flex",alignItems:"center"}}>
                        <Ico d={I.edit} s={15} c={PC.navy}/>
                      </button>
                      <button onClick={()=>{}}
                        style={{background:"none",border:"none",cursor:"pointer",color:PC.textSub,padding:4,borderRadius:6,
                          display:"flex",alignItems:"center"}}>
                        <Ico d={I.eye} s={15} c={PC.green}/>
                      </button>
                      <button onClick={()=>setDelBook(b)}
                        style={{background:"none",border:"none",cursor:"pointer",padding:4,borderRadius:6,
                          display:"flex",alignItems:"center"}}>
                        <Ico d={I.trash} s={15} c={PC.statusRed}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtBooks.length===0&&(
                <tr><td colSpan={8} style={{textAlign:"center",padding:"48px 24px",color:PC.textSub,fontSize:13}}>
                  No books found. Try adjusting your search or filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ── ISSUE & RETURN PAGE ────────────────────────────────────
  const IssuePage = () => (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <Breadcrumb items={[{label:"Dashboard",onClick:()=>setPage("dashboard")},{label:"Issue & Return"}]} onBack={()=>setPage("dashboard")}/>
          <h1 style={{margin:"8px 0 4px",fontSize:24,fontWeight:800,color:PC.navy}}>Issue & Return</h1>
          <p style={{margin:0,fontSize:13,color:PC.textSub}}>Track book loans, returns, and overdue items.</p>
        </div>
        <Btn variant="green" icon={I.plus} onClick={()=>setIssueDialog(true)}>+ New Issue</Btn>
      </div>

      {/* Summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
        {[
          {label:"Active Loans",val:txns.filter(t=>t.status==="issued").length,c:PC.navy},
          {label:"Overdue",val:txns.filter(t=>t.status==="overdue").length,c:PC.statusRed},
          {label:"Returned",val:txns.filter(t=>t.status==="returned").length,c:PC.green},
          {label:"Total Fines",val:`$${txns.reduce((s,t)=>s+t.fine,0)}`,c:PC.statusOrange},
        ].map(s=>(
          <div key={s.label} style={{background:"white",borderRadius:12,padding:"18px 20px",
            border:`1px solid ${PC.border}`,boxShadow:"0 2px 6px rgba(26,39,68,0.04)",textAlign:"center"}}>
            <div style={{fontSize:26,fontWeight:800,color:s.c,marginBottom:4}}>{s.val}</div>
            <div style={{fontSize:12,color:PC.textSub,fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div style={{display:"flex",gap:8,marginBottom:18}}>
        {["all","issued","overdue","returned"].map(f=>(
          <button key={f} onClick={()=>setTxnFilter(f)}
            style={{padding:"6px 14px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
              border:`1.5px solid ${txnFilter===f?PC.navy:PC.border}`,
              background:txnFilter===f?PC.navy:"white",
              color:txnFilter===f?"white":PC.textSub,transition:"all 0.15s"}}>
            {f==="all"?"All":f.charAt(0).toUpperCase()+f.slice(1)}
          </button>
        ))}
      </div>

      {/* Transactions table */}
      <div style={{background:"white",borderRadius:14,border:`1px solid ${PC.border}`,
        boxShadow:"0 2px 8px rgba(26,39,68,0.04)",overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead style={{background:PC.surface}}>
            <tr><TH>Book</TH><TH>Member</TH><TH>Issue Date</TH><TH>Due Date</TH><TH>Return Date</TH><TH>Fine</TH><TH>Status</TH><TH>Action</TH></tr>
          </thead>
          <tbody>
            {filtTxns.map(t=>(
              <tr key={t.id} style={{background:t.status==="overdue"?"#fff8f8":"white",
                borderLeft:t.status==="overdue"?`3px solid ${PC.statusRed}`:"3px solid transparent"}}>
                <TD bold maxW={200}>{t.bookTitle}</TD>
                <TD color={PC.textSub}>{t.memberName}</TD>
                <TD color={PC.textSub}>{fmtDateShort(t.issueDate)}</TD>
                <TD color={t.status==="overdue"?PC.statusRed:PC.textSub} bold={t.status==="overdue"}>
                  {fmtDateShort(t.dueDate)}
                  {t.status==="overdue"&&(
                    <div style={{fontSize:10,color:PC.statusRed}}>
                      {Math.floor((new Date()-new Date(t.dueDate))/86400000)}d overdue
                    </div>
                  )}
                </TD>
                <TD color={PC.textSub}>{fmtDateShort(t.returnDate)}</TD>
                <TD color={t.fine>0?PC.statusRed:PC.textSub} bold={t.fine>0}>
                  {t.fine>0?`$${t.fine}`:"—"}
                </TD>
                <TD><StatusBadge status={t.status}/></TD>
                <td style={{padding:"12px 14px",borderBottom:`1px solid ${PC.borderLight}`}}>
                  {(t.status==="issued"||t.status==="overdue")&&(
                    <button onClick={()=>returnBook(t.id)}
                      style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:7,
                        border:`1.5px solid ${PC.green}`,background:"white",color:PC.green,
                        fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      <Ico d={I.return} s={12} c={PC.green}/> Return
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── BORROWERS PAGE ─────────────────────────────────────────
  const BorrowersPage = () => {
    const filtMems = members.filter(m=>{
      const q=memSearch.toLowerCase();
      return !q||m.name.toLowerCase().includes(q)||m.studentId.toLowerCase().includes(q)||m.department.toLowerCase().includes(q);
    });
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
          <div>
            <Breadcrumb items={[{label:"Dashboard",onClick:()=>setPage("dashboard")},{label:"Members"}]} onBack={()=>setPage("dashboard")}/>
            <h1 style={{margin:"8px 0 4px",fontSize:24,fontWeight:800,color:PC.navy}}>Club Members</h1>
            <p style={{margin:0,fontSize:13,color:PC.textSub}}>View and manage library members and borrowing history.</p>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:selMember?"1fr 400px":"1fr",gap:20,alignItems:"start"}}>
          <div>
            {/* Search bar — matches PeerCampus search style */}
            <div style={{background:"white",borderRadius:14,border:`1px solid ${PC.border}`,
              boxShadow:"0 2px 8px rgba(26,39,68,0.04)",marginBottom:4}}>
              <div style={{padding:"16px 20px",borderBottom:`1px solid ${PC.border}`,
                display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                <div style={{position:"relative",flex:1}}>
                  <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)"}}>
                    <Ico d={I.search} s={15} c={PC.textSub}/>
                  </span>
                  <input placeholder="Search members..." value={memSearch} onChange={e=>setMemSearch(e.target.value)}
                    style={{width:"100%",padding:"8px 12px 8px 34px",borderRadius:8,border:`1.5px solid ${PC.border}`,
                      fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <select style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${PC.border}`,fontSize:13,cursor:"pointer"}}>
                  <option>All</option><option>active</option><option>suspended</option>
                </select>
                {memSearch&&<button onClick={()=>setMemSearch("")} style={{background:"none",border:"none",color:PC.textSub,cursor:"pointer",fontSize:13}}>Clear</button>}
              </div>

              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead style={{background:PC.surface}}>
                  <tr><TH>Member Name</TH><TH>Student ID</TH><TH>Department</TH><TH>Borrowed</TH><TH>Status</TH><TH>Actions</TH></tr>
                </thead>
                <tbody>
                  {filtMems.map(m=>{
                    const active=txns.filter(t=>t.memberId===m.id&&t.status!=="returned").length;
                    const overdue=txns.filter(t=>t.memberId===m.id&&t.status==="overdue").length;
                    const isSel=selMember?.id===m.id;
                    return (
                      <tr key={m.id} style={{background:isSel?PC.greenLight:"white",cursor:"pointer"}}
                        onClick={()=>setSelMember(isSel?null:m)}>
                        <TD bold>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <div style={{width:32,height:32,borderRadius:"50%",flexShrink:0,
                              background:m.status==="suspended"?PC.statusRed:PC.navy,
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:12,fontWeight:700,color:"white"}}>
                              {m.name.split(" ").map(n=>n[0]).join("")}
                            </div>
                            <div>
                              <div style={{fontSize:13,fontWeight:600,color:PC.navy}}>{m.name}</div>
                              <div style={{fontSize:11,color:PC.textSub}}>{m.email}</div>
                            </div>
                          </div>
                        </TD>
                        <TD color={PC.textSub}>{m.studentId}</TD>
                        <TD color={PC.textSub}>{m.department}</TD>
                        <TD>
                          <span style={{fontWeight:700,color:overdue>0?PC.statusRed:active>0?PC.navy:PC.textSub}}>
                            {active}
                            {overdue>0&&<span style={{fontSize:10,color:PC.statusRed,marginLeft:4}}>({overdue} OD)</span>}
                          </span>
                        </TD>
                        <TD><StatusBadge status={m.status}/></TD>
                        <td style={{padding:"12px 14px",borderBottom:`1px solid ${PC.borderLight}`}}>
                          <button style={{background:"none",border:"none",cursor:"pointer",color:PC.textSub}}>
                            <Ico d={I.dots} s={18} c={PC.textSub}/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Member detail panel */}
          {selMember&&(
            <div style={{background:"white",borderRadius:14,border:`2px solid ${PC.green}`,
              boxShadow:"0 4px 20px rgba(45,122,79,0.12)",position:"sticky",top:80}}>
              <div style={{background:PC.heroGrad,borderRadius:"12px 12px 0 0",padding:"20px 22px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:48,height:48,borderRadius:"50%",background:"rgba(255,255,255,0.2)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:17,fontWeight:800,color:"white",border:"2px solid rgba(255,255,255,0.4)"}}>
                      {selMember.name.split(" ").map(n=>n[0]).join("")}
                    </div>
                    <div>
                      <div style={{fontSize:17,fontWeight:800,color:"white"}}>{selMember.name}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.7)"}}>{selMember.email}</div>
                    </div>
                  </div>
                  <button onClick={()=>setSelMember(null)} style={{background:"none",border:"none",cursor:"pointer"}}>
                    <Ico d={I.close} s={16} c="rgba(255,255,255,0.8)"/>
                  </button>
                </div>
              </div>
              <div style={{padding:"18px 22px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                  {[
                    {l:"Student ID",v:selMember.studentId},{l:"Department",v:selMember.department},
                    {l:"Year",v:`Year ${selMember.year}`},{l:"Member Since",v:fmtDate(selMember.memberSince)},
                    {l:"Total Borrowed",v:selMember.totalBorrowed},{l:"Status",v:<StatusBadge status={selMember.status}/>},
                  ].map(({l,v})=>(
                    <div key={l} style={{background:PC.surface,borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,color:PC.textSub,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:3}}>{l}</div>
                      {typeof v==="string"||typeof v==="number"
                        ?<div style={{fontSize:13,fontWeight:700,color:PC.navy}}>{v}</div>
                        :<div style={{marginTop:2}}>{v}</div>}
                    </div>
                  ))}
                </div>
                <div style={{borderTop:`1px solid ${PC.border}`,paddingTop:14}}>
                  <div style={{fontSize:13,fontWeight:800,color:PC.navy,marginBottom:10}}>
                    Borrowing History ({txns.filter(t=>t.memberId===selMember.id).length})
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:280,overflow:"auto"}}>
                    {txns.filter(t=>t.memberId===selMember.id).length===0
                      ?<div style={{textAlign:"center",color:PC.textSub,fontSize:12,padding:"20px 0"}}>No transactions</div>
                      :txns.filter(t=>t.memberId===selMember.id).map(t=>(
                        <div key={t.id} style={{padding:"10px 12px",borderRadius:8,
                          background:t.status==="overdue"?"#fff5f5":PC.surface,
                          border:`1px solid ${t.status==="overdue"?"#ffd5d5":PC.border}`,
                          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div>
                            <div style={{fontSize:12,fontWeight:600,color:PC.navy,marginBottom:2,
                              maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.bookTitle}</div>
                            <div style={{fontSize:10,color:PC.textSub}}>{fmtDateShort(t.issueDate)} → {fmtDateShort(t.dueDate)}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <StatusBadge status={t.status}/>
                            {t.fine>0&&<div style={{fontSize:10,color:PC.statusRed,fontWeight:700,marginTop:3}}>${t.fine} fine</div>}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── SIDEBAR NAV (matches PeerCampus admin with collapsible) ─
  const NAV = [
    {id:"dashboard",label:"Dashboard",icon:I.dashboard},
    {id:"books",label:"Books",icon:I.book},
    {id:"issue",label:"Issue & Return",icon:I.calendar,badge:stats.overdue||null},
    {id:"members",label:"Members",icon:I.users},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",minHeight:"100vh",background:PC.bgPage,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <style>{`
        * { box-sizing: border-box; }
        button { font-family: inherit; }
        input, select { font-family: inherit; }
        @keyframes slideIn { from{transform:translateX(20px);opacity:0} to{transform:none;opacity:1} }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #f0f4f8; }
        ::-webkit-scrollbar-thumb { background: #c8d6e8; border-radius: 3px; }
        tr:hover td { background: #f8fafc !important; }
      `}</style>

      <Navbar/>

      <div style={{display:"flex",flex:1}}>
        {/* Sidebar — navy, matches PeerCampus admin sidebar */}
        <aside style={{width:220,background:PC.bgSidebar,flexShrink:0,display:"flex",flexDirection:"column",
          minHeight:"calc(100vh - 60px)",position:"sticky",top:60}}>
          <div style={{padding:"16px 14px 8px"}}>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,
              textTransform:"uppercase",padding:"0 8px",marginBottom:8}}>Library</div>
            {NAV.map(n=>(
              <div key={n.id} onClick={()=>setPage(n.id)}
                style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,
                  marginBottom:3,cursor:"pointer",position:"relative",
                  background:page===n.id?"rgba(45,122,79,0.22)":"transparent",
                  borderLeft:page===n.id?`3px solid ${PC.green}`:"3px solid transparent",
                  transition:"all 0.15s"}}>
                <Ico d={n.icon} s={16} c={page===n.id?PC.green:"rgba(255,255,255,0.5)"}/>
                <span style={{fontSize:13,fontWeight:page===n.id?700:400,
                  color:page===n.id?"white":"rgba(255,255,255,0.55)",flex:1}}>{n.label}</span>
                {n.badge&&(
                  <span style={{background:PC.statusRed,color:"white",borderRadius:99,
                    padding:"1px 6px",fontSize:10,fontWeight:800}}>{n.badge}</span>
                )}
              </div>
            ))}
          </div>

          {/* Management section — like PeerCampus sidebar */}
          <div style={{padding:"8px 14px",borderTop:"1px solid rgba(255,255,255,0.07)",marginTop:8}}>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,
              textTransform:"uppercase",padding:"0 8px",marginBottom:8}}>Quick Stats</div>
            <div style={{background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"12px 14px"}}>
              {[
                {l:"Total Books",v:stats.totalBooks,c:"rgba(255,255,255,0.9)"},
                {l:"Issued",v:stats.issued,c:"rgba(255,255,255,0.9)"},
                {l:"Overdue",v:stats.overdue,c:stats.overdue>0?"#ff6b6b":"rgba(255,255,255,0.9)"},
              ].map(s=>(
                <div key={s.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>{s.l}</span>
                  <span style={{fontSize:13,fontWeight:700,color:s.c}}>{s.v}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{marginTop:"auto",padding:"12px 14px",borderTop:"1px solid rgba(255,255,255,0.07)"}}>
            <div style={{background:"rgba(45,122,79,0.15)",borderRadius:9,padding:"10px 12px"}}>
              <div style={{fontSize:11,color:PC.green,fontWeight:700}}>PeerCampus Library</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:2}}>Lucknow University · v2.1</div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main style={{flex:1,padding:"28px 32px",minWidth:0,overflow:"auto"}}>
          {page==="dashboard" && <DashboardPage/>}
          {page==="books"     && <BooksPage/>}
          {page==="issue"     && <IssuePage/>}
          {page==="members"   && <BorrowersPage/>}
        </main>
      </div>

      <Footer/>

      {/* ── MODALS ─────────────────────────────────────────── */}

      {/* Book Add/Edit Modal */}
      <Modal open={bookDialog.open} onClose={()=>setBookDialog({open:false,edit:null})}
        title={bookDialog.edit?"Edit Book":"Add New Book"}
        footer={<>
          <Btn variant="outline" onClick={()=>setBookDialog({open:false,edit:null})}>Cancel</Btn>
          <Btn variant="navy" onClick={saveBook}>{bookDialog.edit?"Save Changes":"Add Book"}</Btn>
        </>}>
        <div style={{display:"grid",gap:14}}>
          <Field label="Book Title" value={bForm.title} onChange={v=>setBForm(f=>({...f,title:v}))} required error={bErrors.title} placeholder="e.g. Introduction to Algorithms"/>
          <Field label="Author" value={bForm.author} onChange={v=>setBForm(f=>({...f,author:v}))} required error={bErrors.author} placeholder="e.g. Thomas H. Cormen"/>
          <Field label="ISBN" value={bForm.isbn} onChange={v=>setBForm(f=>({...f,isbn:v}))} required error={bErrors.isbn} placeholder="978-0-000-00000-0"/>
          <Field label="Category" value={bForm.category} onChange={v=>setBForm(f=>({...f,category:v}))} options={CATS}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Total Copies" value={bForm.totalCopies} onChange={v=>setBForm(f=>({...f,totalCopies:+v}))} type="number" required error={bErrors.totalCopies}/>
            <Field label="Published Year" value={bForm.publishedYear} onChange={v=>setBForm(f=>({...f,publishedYear:+v}))} type="number"/>
          </div>
          <Field label="Publisher" value={bForm.publisher} onChange={v=>setBForm(f=>({...f,publisher:v}))} placeholder="e.g. MIT Press"/>
          <Field label="Shelf Location" value={bForm.location} onChange={v=>setBForm(f=>({...f,location:v}))} placeholder="e.g. A1-01"/>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!delBook} onClose={()=>setDelBook(null)} title="Delete Book?"
        footer={<>
          <Btn variant="outline" onClick={()=>setDelBook(null)}>Cancel</Btn>
          <Btn variant="danger" onClick={deleteBook}>Delete</Btn>
        </>}>
        <p style={{margin:0,fontSize:13,color:PC.textSub,lineHeight:1.6}}>
          Are you sure you want to delete <strong style={{color:PC.navy}}>"{delBook?.title}"</strong>?
          This action cannot be undone.
        </p>
      </Modal>

      {/* Issue Book Modal */}
      <Modal open={issueDialog} onClose={()=>{setIssueDialog(false);setIssueErr("");}} title="Issue Book" width={420}
        footer={<>
          <Btn variant="outline" onClick={()=>setIssueDialog(false)}>Cancel</Btn>
          <Btn variant="green" onClick={issueBook}>Issue Book →</Btn>
        </>}>
        <div style={{display:"grid",gap:14}}>
          {issueErr&&(
            <div style={{background:"#fff5f5",border:"1px solid #ffd5d5",borderRadius:8,padding:"10px 14px",
              fontSize:13,color:PC.statusRed,display:"flex",alignItems:"center",gap:8}}>
              <Ico d={I.alert} s={14} c={PC.statusRed}/>{issueErr}
            </div>
          )}
          <div>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:PC.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>
              Select Book *
            </label>
            <select value={issueForm.bookId} onChange={e=>setIssueForm(f=>({...f,bookId:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${PC.border}`,fontSize:13,outline:"none"}}>
              <option value="">— Choose a book —</option>
              {books.filter(b=>b.availableCopies>0).map(b=>(
                <option key={b.id} value={b.id}>{b.title} ({b.availableCopies} available)</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:PC.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>
              Select Member *
            </label>
            <select value={issueForm.memberId} onChange={e=>setIssueForm(f=>({...f,memberId:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${PC.border}`,fontSize:13,outline:"none"}}>
              <option value="">— Choose a member —</option>
              {members.filter(m=>m.status==="active").map(m=>(
                <option key={m.id} value={m.id}>{m.name} · {m.studentId}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:PC.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>
              Loan Period
            </label>
            <select value={issueForm.dueDays} onChange={e=>setIssueForm(f=>({...f,dueDays:+e.target.value}))}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${PC.border}`,fontSize:13,outline:"none"}}>
              {[7,14,21,30].map(d=><option key={d} value={d}>{d} days (Due: {new Date(Date.now()+d*86400000).toLocaleDateString("en-CA")})</option>)}
            </select>
          </div>
        </div>
      </Modal>

      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
}
